import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Image } from '../../../../modules/design-system';
import type { PoolOfferListItem } from '../../../../modules/api';

export interface SliderProps {
  offers: PoolOfferListItem[];
  activeOfferId: number | null;
  onSlideChange?: (offerId: number) => void;
}

/** Slide-to-slide transition duration after release. */
const SLIDE_TRANSITION_MS = 360;
/** Drag must exceed this fraction of container width to commit a swipe. */
const SNAP_FRACTION = 0.15;
/** Max slides rendered on each side of the active one. Only one peek
    per side — Figma 543:2118 shows exactly one neighbour visible left
    and right of the centre pack. */
const MAX_RENDER_WINDOW = 1;
/** Rubber-band coefficient when dragging past the first / last slot. */
const OVERDRAG_RESIST = 0.35;

const TRANSITION_OUT = `transform ${SLIDE_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${SLIDE_TRANSITION_MS}ms ease-out`;

/** Default pedestal shown under a pack when the operator hasn't configured a
 *  custom one (PoolOffer.pedestalImageUrl is null). Served from
 *  apps/iframe/public, so it resolves at the app root. */
const DEFAULT_PEDESTAL_URL = '/templates/modern/default-piedestal.png';

/**
 * Figma-exact pixel geometry per breakpoint. Every value is lifted from
 * the Figma node it came from so the layout is independent of viewport
 * width — the pack always renders at its Figma size, the slider always
 * occupies its Figma height. This is what makes the page look "like the
 * mockup" on every common viewport, not a stretched mobile rectangle.
 *
 * `slotStep` is the horizontal distance (px) between the centre pack
 * and its immediate peek; kept fixed so a narrow viewport hides part of
 * the peek (matches Figma mobile) and a wide viewport reveals it fully
 * (matches Figma desktop).
 */
interface Geometry {
  sliderHeight: number;
  pack: { w: number; h: number; top: number };
  pedestal: { w: number; h: number; top: number };
  logo: { w: number; h: number; top: number } | null;
  slotStep: number;
  packPeekScale: number;
  pedestalPeekScale: number;
  /** Vertical shift (px) of the peek pack relative to the centre pack. */
  peekPackYPx: number;
  /** Vertical shift (px) of the peek pedestal relative to centre. */
  peekPedestalYPx: number;
}

// Figma mobile frame 425:946 (375 × 812). Same approach as DESKTOP:
//   • pack box aspect 0.833 = natural pack PNG (844/1012) — no
//     `object-contain` letterbox, pack fills the box edge-to-edge.
//   • pack box is bumped ~1.16× off Figma so the pack reads as a focal
//     point on phones; pedestal stays as a "stage" wider than the pack
//     (Figma intent for mobile, vs the narrow "saucer" on desktop).
//   • pack bottom physically overlaps pedestal top by 20 px so the pack
//     sits ON the pedestal, not above it.
//   • logo banner is enlarged to match (kept at the Figma 2:1 aspect).
const MOBILE: Geometry = {
  // Trimmed so the slider ends just below the title (pack+pedestal+title reach
  // ~389px), removing the empty gap before the Potential Hits section. Was 460
  // when the brand sat as a logo on the pedestal (lower); the title sits higher.
  sliderHeight: 400,
  pack:     { w: 220, h: 264, top:   0 }, // bottom y=264, aspect 0.833
  pedestal: { w: 252, h:  80, top: 262 }, // mobile: smaller + lower so the pack floats just above (Figma 786:55598)
  logo:     { w: 240, h: 120, top: 269 }, // fits the trimmed 400px slider; bottom (~389) aligns with the title fallback. Tune to Figma if a with-logo mobile frame exists.
  slotStep: 252,                          // scaled with pack width (217 × 220/189)
  packPeekScale:     0.81,                // Figma peek pack 153/189
  pedestalPeekScale: 0.56,                // Figma peek pedestal 149/264 — aggressive shrink
  // Peek pack moves UP (smaller pack sits *above* its peek pedestal).
  // Figma 425: peek bottom y=336, centre bottom y=382 → -46 px diff.
  // Without this the peek pack inherits the centre's bottom anchor and
  // crashes through the peek pedestal.
  peekPackYPx:      -46,
  peekPedestalYPx:  -34,                  // peek pedestal offset (Figma 425:1081)
};

// Tablet (600 ≤ viewport < 900). No Figma reference at this width, so
// values are interpolated: pack ~30% of typical tablet viewport (768),
// pedestal still a wide "stage" like mobile, but everything bumped up.
const TABLET: Geometry = {
  sliderHeight: 560,
  pack:     { w: 290, h: 348, top:   0 }, // aspect 0.833
  pedestal: { w: 370, h: 125, top: 326 }, // top 22px above pack bottom → overlap
  logo:     { w: 320, h: 160, top: 400 }, // aspect 2:1, sits on pedestal
  slotStep: 330,
  packPeekScale:     0.78,                // slightly closer to centre than mobile
  pedestalPeekScale: 0.60,
  // Negative for the same reason as mobile — peek pack hovers above its
  // peek pedestal instead of crashing into it.
  peekPackYPx:      -55,
  peekPedestalYPx:  -38,
};

// Figma desktop frame 543:2118 (1535 × 1086). Slider area runs from
// y=0 to ~y=540. Pack box aspect is matched to the source PNG's natural
// 844:1012 (= 0.833) so `object-contain` doesn't letterbox the pack
// (the old 265:332 box left a 7px transparent gap top+bottom that made
// the pack look like it was floating). The box is also scaled ~1.2× off
// Figma so the centre pack reads as a focal point on wide desktop
// viewports, and is positioned so its bottom physically overlaps the
// pedestal top by 24px — that's what makes the pack visually sit *on*
// the pedestal instead of hovering above it.
const DESKTOP: Geometry = {
  sliderHeight: 580,
  pack:     { w: 360, h: 432, top:  50 }, // bottom y=482, aspect 0.833 = natural pack PNG
  pedestal: { w: 325, h: 109, top: 460 }, // top 22px above pack bottom → overlap
  logo: null,                             // desktop logo lives in PackInfoCard
  slotStep: 460,                          // tuned so right/left peeks read like Figma 543:2118 at typical desktop widths
  packPeekScale:     0.67,                // Figma peek pack 177/265
  pedestalPeekScale: 0.68,                // Figma peek pedestal 175/258
  // Peek pack rises above its peek pedestal — Figma 543: peek bottom
  // y=344, centre bottom y=382 → -38 in Figma px, scaled to our larger
  // pack height (432 vs Figma 332) → ~-49.
  peekPackYPx:      -50,
  peekPedestalYPx:  -34,                  // peek pedestal offset (Figma 543:2203)
};

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Coverflow slider with pixel-perfect Figma geometry per breakpoint.
 *
 *   • viewport < 900 — mobile layout (Figma `425:946`, slider 480 px tall).
 *   • viewport ≥ 900 — desktop layout (Figma `543:2118`, slider 600 px tall).
 *
 * Pack / pedestal / logo are rendered with fixed pixel widths & heights
 * from Figma. Each slot is absolutely positioned at the slider's
 * horizontal centre and translated sideways by
 * `distance × slotStep + dragX`. The slot's vertical layout is
 * unchanged across viewport widths so the pack always reads at its
 * Figma size, independent of how much empty space surrounds it.
 *
 * Inside each slot, the pedestal / pack / logo apply per-layer scale,
 * opacity and translateY pegged to the slot's `centeredness` — a
 * fractional value that updates in real time during drag so peeks
 * shrink (and slide down/up) as the user pulls them away from the
 * centre.
 *
 * Swipe interactions only mutate the slider's own `currentIndex` and
 * fire `onSlideChange`; the parent updates `gameId` in-state without
 * touching the URL.
 */
export function Slider({
  offers,
  activeOfferId,
  onSlideChange,
}: SliderProps) {
  const len = offers.length;
  const externalIndex =
    activeOfferId !== null && len > 0
      ? Math.max(0, offers.findIndex((o) => o.id === activeOfferId))
      : 0;

  const [currentIndex, setCurrentIndex] = useState(externalIndex);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [tier, setTier] = useState<'mobile' | 'tablet' | 'desktop'>('mobile');
  const startX = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentIndex(externalIndex);
  }, [externalIndex]);

  useEffect(() => {
    const desktopMql = window.matchMedia('(min-width: 900px)');
    const tabletMql = window.matchMedia('(min-width: 600px)');
    const update = () => {
      if (desktopMql.matches) setTier('desktop');
      else if (tabletMql.matches) setTier('tablet');
      else setTier('mobile');
    };
    update();
    desktopMql.addEventListener('change', update);
    tabletMql.addEventListener('change', update);
    return () => {
      desktopMql.removeEventListener('change', update);
      tabletMql.removeEventListener('change', update);
    };
  }, []);

  const dims =
    tier === 'desktop' ? DESKTOP : tier === 'tablet' ? TABLET : MOBILE;
  const isDesktop = tier === 'desktop';

  const go = (toIndex: number) => {
    if (len === 0) return;
    const next = Math.max(0, Math.min(len - 1, toIndex));
    if (next === currentIndex) return;
    setCurrentIndex(next);
    const target = offers[next];
    if (target) onSlideChange?.(target.id);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (len < 2) return;
    startX.current = e.clientX;
    setIsDragging(true);
    setDragX(0);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (startX.current === null) return;
    const raw = e.clientX - startX.current;
    let dx = raw;
    if (currentIndex === 0 && raw > 0) dx = raw * OVERDRAG_RESIST;
    else if (currentIndex === len - 1 && raw < 0) dx = raw * OVERDRAG_RESIST;
    if (dx > dims.slotStep) dx = dims.slotStep;
    if (dx < -dims.slotStep) dx = -dims.slotStep;
    setDragX(dx);
  };

  const finishDrag = (e: React.PointerEvent) => {
    if (startX.current === null) return;
    const raw = e.clientX - startX.current;
    const width = containerRef.current?.offsetWidth ?? 1;
    startX.current = null;
    setIsDragging(false);
    setDragX(0);
    const threshold = width * SNAP_FRACTION;
    if (Math.abs(raw) > threshold) {
      go(raw > 0 ? currentIndex - 1 : currentIndex + 1);
    }
  };

  if (len === 0) {
    return (
      <div ref={containerRef} className="relative w-full select-none">
        <div className="relative w-full" style={{ height: dims.sliderHeight }} />
      </div>
    );
  }

  const transitionForLayer = isDragging ? 'none' : TRANSITION_OUT;

  return (
    <div ref={containerRef} className="relative w-full select-none">
      <div
        // Clip the peek slots HORIZONTALLY so a side pack (esp. the right
        // peek, which translateX's past the viewport) can't widen the document
        // — a wider document makes the `fixed left-0 right-0` bottom bar span
        // the whole scroll-width (e.g. 556px) and pushes its RIP/plateau
        // off-screen. `clip` + default `overflow-y:visible` keeps vertical pack
        // shadows intact (they coexist per spec). Desktop keeps `visible` —
        // there the bottom bar is in-flow, not fixed, so no widening problem.
        className="relative w-full overflow-x-clip min-[900px]:overflow-x-visible"
        style={{ height: dims.sliderHeight, touchAction: 'pan-y' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
      >
        {/* HeroBackdrop (sonar waves) is rendered by PackPage one level
            up so it bleeds *behind* the header and *past* the slider's
            own bounds, matching Figma 543:2118. Slider itself only
            renders the slots over the wave layer. */}

        {/* Chevron arrows — desktop only. Positioned right next to the
            pack (40 px gap from each pack edge) instead of at the slider
            container edges, matching Figma 543:2118 where the arrows sit
            tight against the centre pack. */}
        {isDesktop && len > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(currentIndex - 1)}
              // Stop pointerdown bubbling so the slider's
              // `setPointerCapture` doesn't swallow the button's click.
              onPointerDown={(e) => e.stopPropagation()}
              disabled={currentIndex === 0}
              aria-label="Previous pack"
              style={{ left: `calc(50% - ${dims.pack.w / 2 + 60}px)` }}
              className="
                absolute top-1/2 -translate-y-1/2 -translate-x-full z-30
                flex h-11 w-11 items-center justify-center rounded-full
                bg-black/30 text-white backdrop-blur-md
                border border-white/15
                cursor-pointer transition
                hover:bg-black/45 hover:scale-105
                disabled:cursor-default disabled:opacity-30 disabled:hover:scale-100
              "
            >
              <ChevronLeft size={20} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={() => go(currentIndex + 1)}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={currentIndex === len - 1}
              aria-label="Next pack"
              style={{ left: `calc(50% + ${dims.pack.w / 2 + 60}px)` }}
              className="
                absolute top-1/2 -translate-y-1/2 z-30
                flex h-11 w-11 items-center justify-center rounded-full
                bg-black/30 text-white backdrop-blur-md
                border border-white/15
                cursor-pointer transition
                hover:bg-black/45 hover:scale-105
                disabled:cursor-default disabled:opacity-30 disabled:hover:scale-100
              "
            >
              <ChevronRight size={20} strokeWidth={2.5} />
            </button>
          </>
        )}

        {offers.map((slotOffer, sourceIndex) => {
          const distance = sourceIndex - currentIndex;
          if (Math.abs(distance) > MAX_RENDER_WINDOW) return null;

          const slotOffsetPx = distance * dims.slotStep + dragX;
          const dragRatio = dragX / dims.slotStep;
          const centeredness = Math.max(0, 1 - Math.abs(distance + dragRatio));
          const peekness = 1 - centeredness;

          const pedestalScale = lerp(dims.pedestalPeekScale, 1, centeredness);
          const pedestalOpacity = lerp(0.3, 1, centeredness);
          const packScale = lerp(dims.packPeekScale, 1, centeredness);
          const packOpacity = lerp(0.55, 1, centeredness);
          const logoOpacity =
            centeredness > 0.7 ? (centeredness - 0.7) / 0.3 : 0;

          const packYShift = peekness * dims.peekPackYPx;
          const pedestalYShift = peekness * dims.peekPedestalYPx;

          const slotZ = centeredness > 0.5 ? 20 : 10;

          return (
            <div
              key={`slot-${slotOffer.id}`}
              data-distance={distance}
              data-centeredness={centeredness.toFixed(2)}
              className="absolute inset-0"
              style={{
                pointerEvents: 'none',
                zIndex: slotZ,
              }}
              aria-hidden={centeredness < 0.9 || undefined}
            >
              {/* Pedestal — fixed px size, centred horizontally, peek
                  shifts up via translateY. Falls back to the bundled default
                  plinth when the operator hasn't set a custom pedestal image. */}
              <div
                className="absolute"
                style={{
                  top: dims.pedestal.top,
                  left: `calc(50% - ${dims.pedestal.w / 2}px)`,
                  width: dims.pedestal.w,
                  height: dims.pedestal.h,
                  transform: `translateX(${slotOffsetPx}px) translateY(${pedestalYShift}px) scale(${pedestalScale})`,
                  transformOrigin: 'center center',
                  opacity: pedestalOpacity,
                  transition: transitionForLayer,
                  willChange: isDragging ? 'transform' : undefined,
                }}
              >
                <Image
                  src={slotOffer.pedestalImageUrl ?? DEFAULT_PEDESTAL_URL}
                  alt=""
                  className="absolute inset-0"
                  imgClassName="absolute inset-0 h-full w-full object-contain"
                />
              </div>

              {/* Pack — fixed px size, centred horizontally, peek
                  shifts down via translateY. */}
              {slotOffer.packImageUrl && (
                <div
                  className="absolute [filter:drop-shadow(0_24px_24px_rgba(0,0,0,0.35))]"
                  style={{
                    top: dims.pack.top,
                    left: `calc(50% - ${dims.pack.w / 2}px)`,
                    width: dims.pack.w,
                    height: dims.pack.h,
                    transform: `translateX(${slotOffsetPx}px) translateY(${packYShift}px) scale(${packScale})`,
                    transformOrigin: 'center bottom',
                    opacity: packOpacity,
                    transition: transitionForLayer,
                    willChange: isDragging ? 'transform' : undefined,
                  }}
                >
                  <Image
                    src={slotOffer.packImageUrl}
                    alt={centeredness > 0.9 ? slotOffer.title : ''}
                    className="absolute inset-0"
                    imgClassName="absolute inset-0 h-full w-full object-contain"
                    loading={centeredness > 0.5 ? 'eager' : 'lazy'}
                  />
                </div>
              )}

              {/* Logo banner — mobile/tablet only, fixed px size, centred.
                  Falls back to the pack TITLE (text) when the operator hasn't
                  set a logo image, so the pack is never nameless on mobile
                  (desktop shows the title via PackInfoCard instead). */}
              {dims.logo &&
                (slotOffer.packLogoImageUrl ? (
                  <div
                    className="absolute"
                    style={{
                      top: dims.logo.top,
                      left: `calc(50% - ${dims.logo.w / 2}px)`,
                      width: dims.logo.w,
                      height: dims.logo.h,
                      transform: `translateX(${slotOffsetPx}px)`,
                      opacity: logoOpacity,
                      transition: transitionForLayer,
                    }}
                  >
                    <Image
                      src={slotOffer.packLogoImageUrl}
                      alt=""
                      className="absolute inset-0"
                      imgClassName="absolute inset-0 h-full w-full object-contain"
                    />
                  </div>
                ) : (
                  <div
                    data-co-component="PackTitle"
                    className="absolute left-1/2 flex items-center justify-center px-4 text-center"
                    style={{
                      // Sit just below the pedestal (Figma 786:55598 places the
                      // title under the plinth), not in the on-pedestal logo slot.
                      // `left-1/2` + translateX(-50%) keeps the box hugging the
                      // text (a tight hover-outline / click target) while still
                      // sliding with the carousel.
                      top: dims.pedestal.top + dims.pedestal.h + 14,
                      transform: `translateX(calc(-50% + ${slotOffsetPx}px))`,
                      opacity: logoOpacity,
                      transition: transitionForLayer,
                      // The parent slot is pointer-events:none; re-enable on the
                      // title so the Design Constructor's hover outline +
                      // right-click-to-edit can target it. Pointerdown still
                      // bubbles to the slider container, so swiping keeps working.
                      pointerEvents: 'auto',
                    }}
                  >
                    {/* Pixel-perfect to Figma node 786:55752 (values ÷1.154 to
                        the app's base scale): Inter Bold, 24px, -0.36px tracking.
                        Font family / size / weight / colour are themeable via the
                        Design Constructor → "Pack title (mobile)" (--co-PackTitle-*);
                        fallbacks reproduce the Figma defaults for unmodified deploys. */}
                    <span
                      className="uppercase whitespace-nowrap"
                      style={{
                        fontFamily: 'var(--co-PackTitle-fontFamily, Inter)',
                        fontSize: 'var(--co-PackTitle-size, 24px)',
                        fontWeight: 'var(--co-PackTitle-weight, 700)',
                        color: 'var(--co-PackTitle-color, #ffffff)',
                        lineHeight: '33.5px',
                        letterSpacing: '-0.36px',
                      }}
                    >
                      {slotOffer.title}
                    </span>
                  </div>
                ))}
            </div>
          );
        })}
      </div>

      {/* Pagination dots — desktop only. On mobile the dots live in
          PackLayout's PotentialHits header (Figma 629:588), so the
          slider doesn't render any of its own below the pedestal.

          Active / idle colour flows through `--co-SliderDots-{active,idle}`
          so the Design Constructor (SliderDots component) can recolour
          every dot at once. Fallbacks are the original Tailwind tones
          (`bg-white` / `bg-white/40`), so an unmodified deploy stays
          pixel-identical. Hover state on idle dots is reproduced via
          a CSS custom-property nudge inside `onMouseEnter`/`Leave` —
          replacing the previous `hover:bg-white/60` arbitrary class. */}
      {len > 1 && (
        <div
          data-co-component="SliderDots"
          className="hidden min-[900px]:flex items-center justify-center gap-[7px] pt-3"
        >
          {offers.map((o, i) => {
            const active = i === currentIndex;
            return (
              <button
                key={o.id}
                type="button"
                aria-label={`Go to ${o.title}`}
                aria-current={active || undefined}
                onClick={() => go(i)}
                className={
                  'rounded-full transition-all cursor-pointer ' +
                  (active ? 'h-2 w-2' : 'h-[6px] w-[6px]')
                }
                style={{
                  background: active
                    ? 'var(--co-SliderDots-active, rgba(255,255,255,1))'
                    : 'var(--co-SliderDots-idle, rgba(255,255,255,0.4))',
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
